// ============================================================================
// src/components/BenchmarkPanel/ComparableTransactionsTable.tsx
// PV-1052 · PR-G — Collapsible table showing up to 8 comparable DLD transactions.
//
// Uses native <details>/<summary> for accessibility (no JS, screen reader OK).
// Anti-leak is enforced server-side by the RPC (no transaction_number / id).
// Mobile : horizontal scroll if columns overflow.
// ============================================================================

import { useTranslation } from 'react-i18next';

import type { DldComparableTransaction } from '@/lib/db/dld-comparison';
import { formatAed, formatNumber, formatPublishedDate } from '@/lib/format';
import './ComparableTransactionsTable.css';

export type ComparableTransactionsTableProps = {
  transactions: DldComparableTransaction[];
};

export function ComparableTransactionsTable({ transactions }: ComparableTransactionsTableProps) {
  const { t, i18n } = useTranslation();

  if (transactions.length === 0) return null;

  return (
    <details className="dld-comparables">
      <summary className="dld-comparables__summary">
        <span className="dld-comparables__summary-text">
          {t('common:benchmark.comparables.show', { count: transactions.length })}
        </span>
        <span className="dld-comparables__summary-text dld-comparables__summary-text--collapsed">
          {t('common:benchmark.comparables.hide')}
        </span>
        <span className="dld-comparables__chevron" aria-hidden="true">
          ▾
        </span>
      </summary>
      <div className="dld-comparables__scroll">
        <table className="dld-comparables__table">
          <thead>
            <tr>
              <th scope="col">{t('common:benchmark.comparables.column.date')}</th>
              <th scope="col">{t('common:benchmark.comparables.column.building')}</th>
              <th scope="col">{t('common:benchmark.comparables.column.type')}</th>
              <th scope="col" className="dld-comparables__num">
                {t('common:benchmark.comparables.column.bedrooms')}
              </th>
              <th scope="col" className="dld-comparables__num">
                {t('common:benchmark.comparables.column.area')}
              </th>
              <th scope="col" className="dld-comparables__num">
                {t('common:benchmark.comparables.column.price')}
              </th>
              <th scope="col" className="dld-comparables__num">
                {t('common:benchmark.comparables.column.price_per_sqft')}
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx, idx) => (
              <tr key={`${tx.transaction_date}-${idx}`}>
                <td>{formatPublishedDate(tx.transaction_date, i18n.language)}</td>
                <td className="dld-comparables__building" title={tx.building ?? ''}>
                  {tx.building ?? '—'}
                </td>
                <td>{tx.property_sub_type}</td>
                <td className="dld-comparables__num">
                  {tx.bedrooms === 0 ? 'Studio' : tx.bedrooms}
                </td>
                <td className="dld-comparables__num">{formatNumber(tx.area_sqft)}</td>
                <td className="dld-comparables__num">{formatAed(tx.sale_price_aed)}</td>
                <td className="dld-comparables__num">
                  {formatNumber(Math.round(tx.price_per_sqft))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
